import { hasBundleComponents } from '@frodo/lib/utils/common/productUtils';
import type { GaEcommerceItem } from './gaEcommerce.interface';
import type { CartItemType, Product } from '@frodo/models/types';

export function mapProductDataToGa4Item(
  product: Product,
  options: {
    currency?: string;
    index?: number;
    quantity?: number;
    isBundle?: boolean;
    parentBundleProduct?: Product;
    itemListId?: string;
    itemListName?: string;
  } = {},
): GaEcommerceItem {
  const {
    currency,
    index,
    quantity = 1,
    isBundle,
    parentBundleProduct,
    itemListId,
    itemListName,
  } = options;

  // PDP products expose euro prices via unitPriceAti/unitPriceTf, but SRP/CRP
  // list items only carry finalGrossPrice/finalNetPrice (integer cents), so fall
  // back to those to keep item price/discount populated.
  const unitPriceAti =
    product.unitPriceAti != null
      ? Number(product.unitPriceAti)
      : (product.finalGrossPrice ?? 0) / 100;
  const unitPriceTf =
    product.unitPriceTf != null
      ? Number(product.unitPriceTf)
      : (product.finalNetPrice ?? 0) / 100;

  const baseItem: GaEcommerceItem = {
    item_id: product.sku,
    item_name: product.name,
    item_list_id: itemListId,
    item_list_name: itemListName ?? '',
    price: unitPriceAti,
    quantity,
    currency,
    index,
    discount: Number((unitPriceAti - unitPriceTf).toFixed(2)),
    item_brand: product.manufacturer,
    item_category: product.categoryLvl1,
    item_category2: product.categoryLvl2,
    item_category3: product.categoryLvl3,
    item_category4: product.categoryLvl4,
  };

  if (isBundle) {
    return {
      ...baseItem,
      is_bundle: true,
      bundle_id: product.sku,
      bundle_name: product.name,
    };
  }

  if (parentBundleProduct) {
    return {
      ...baseItem,
      bundle_id: parentBundleProduct.sku,
      bundle_name: parentBundleProduct.name,
      is_bundle_main_component: product.isBundleMainComponent,
      is_bundle_component: product.isBundleComponent,
    };
  }

  return baseItem;
}

export function getGa4EcommerceItems(
  products: Product[],
  options?: {
    currency?: string;
    quantity?: number;
    itemListId?: string;
    itemListName?: string;
  },
): GaEcommerceItem[] {
  if (products.length === 0) {
    return [];
  }

  const items: GaEcommerceItem[] = [];
  const currency = options?.currency;
  const quantity = options?.quantity || 1;
  const itemListId = options?.itemListId;
  const itemListName = options?.itemListName;

  let currentIndex = 0;

  products.forEach(product => {
    if (hasBundleComponents(product)) {
      const bundleItem = mapProductDataToGa4Item(product, {
        currency,
        quantity,
        index: currentIndex,
        isBundle: true,
        itemListId,
        itemListName,
      });

      items.push(bundleItem);
      currentIndex++;

      product.bundleComponents.forEach(component => {
        const componentItem = mapProductDataToGa4Item(component, {
          currency,
          quantity: 1,
          index: currentIndex,
          parentBundleProduct: product,
          itemListId,
          itemListName,
        });

        items.push(componentItem);
        currentIndex++;
      });
    } else {
      items.push(
        mapProductDataToGa4Item(product, {
          currency,
          quantity,
          index: currentIndex,
          itemListId,
          itemListName,
        }),
      );
      currentIndex++;
    }
  });

  return items;
}

export function mapCartItemToGa4Item(
  cartItem: CartItemType,
  options: { currency?: string; index?: number } = {},
): GaEcommerceItem {
  const { currency, index } = options;

  const baseItem: GaEcommerceItem = {
    item_id: cartItem.sku,
    item_name: cartItem.name,
    price: cartItem.unitNetPrice,
    quantity: cartItem.quantity,
    currency,
    index,
    discount: Number(
      (cartItem.unitGrossPrice - cartItem.unitNetPrice).toFixed(2),
    ),
  };

  if (cartItem.isBundle) {
    return {
      ...baseItem,
      is_bundle: true,
      bundle_id: cartItem.sku,
      bundle_name: cartItem.name,
    };
  }

  return baseItem;
}

export function getGa4EcommerceCartItems(
  cartItems: CartItemType[],
  options?: { currency?: string },
): GaEcommerceItem[] {
  if (cartItems.length === 0) {
    return [];
  }

  const items: GaEcommerceItem[] = [];
  const currency = options?.currency;

  let currentIndex = 0;

  cartItems.forEach(cartItem => {
    items.push(
      mapCartItemToGa4Item(cartItem, {
        currency,
        index: currentIndex,
      }),
    );
    currentIndex++;

    if (cartItem.isBundle && cartItem.bundleComponents) {
      cartItem.bundleComponents.forEach(component => {
        items.push(
          mapCartItemToGa4Item(component, {
            currency,
            index: currentIndex,
          }),
        );
        currentIndex++;
      });
    }
  });

  return items;
}
